import { getQueryClient } from "@/integrations/tanstack-query/root-provider";
import { authClient } from "@/lib/api/auth-client";
import { apiClient } from "@/lib/api/client";
import { type FieldError, parseApiError } from "@/lib/api/error-parser";
import type { components } from "@/lib/api/types";
import { getAuthInitPromise, useAuthStore } from "@/stores/auth-store";
import { useOnboardingStore } from "@/stores/onboarding-store";
import { useStreamingStore } from "@/stores/streaming-store";
import { useTeammateUIStore } from "@/stores/teammate-ui-store";

type UserCreate = components["schemas"]["UserCreate"];
type UserLogin = components["schemas"]["UserLogin"];
type AuthSuccessResponse = components["schemas"]["AuthSuccessResponse"];

export interface LoginCredentials {
	email: string;
	password: string;
}

export interface SignupData {
	email: string;
	password: string;
	firstName: string;
}

export interface AuthError {
	message: string;
	code?: string;
	fieldErrors?: FieldError[];
}

/**
 * Login user with email and password
 */
export async function login(
	credentials: LoginCredentials
): Promise<{ success: true; data: AuthSuccessResponse } | { success: false; error: AuthError }> {
	try {
		const { data, error, response } = await apiClient.POST("/api/v1/auth/login", {
			body: {
				email: credentials.email,
				password: credentials.password,
			} satisfies UserLogin,
		});

		if (error || !data) {
			// Parse API error with field-level details
			const parsedError = parseApiError(error, response);

			// Override message for specific status codes
			if (response.status === 401) {
				parsedError.message = "Invalid email or password";
			} else if (response.status === 403) {
				parsedError.message =
					typeof error?.detail === "string"
						? error.detail
						: "Please verify your email before logging in.";
			} else if (response.status !== 422) {
				// For non-validation errors, show generic message
				parsedError.message = "Login failed. Please try again.";
			}

			return {
				success: false,
				error: {
					message: parsedError.message,
					code: parsedError.code,
					fieldErrors: parsedError.fieldErrors,
				},
			};
		}

		// Update auth store
		useAuthStore.getState().setAuth({
			user: data.user,
			accessToken: data.api_key,
			refreshToken: data.refresh_token,
			accessExpiresAt: data.access_expires_at || "",
			refreshExpiresAt: data.refresh_expires_at || "",
		});

		return { success: true, data };
	} catch (error) {
		console.error("Login error:", error);
		return {
			success: false,
			error: {
				message: "An unexpected error occurred. Please try again.",
			},
		};
	}
}

/**
 * Register a new user
 */
export async function signup(
	data: SignupData
): Promise<{ success: true; data: AuthSuccessResponse } | { success: false; error: AuthError }> {
	try {
		const {
			data: responseData,
			error,
			response,
		} = await apiClient.POST("/api/v1/auth/register", {
			body: {
				email: data.email,
				password: data.password,
				first_name: data.firstName.trim(),
			} satisfies UserCreate,
		});

		if (error || !responseData) {
			// Parse API error with field-level details
			const parsedError = parseApiError(error, response);

			// Override message for 409 conflict (email exists)
			if (response?.status === 409) {
				parsedError.message = "An account with this email already exists";
			} else if (response?.status !== 422) {
				// For non-validation errors, show generic message
				parsedError.message = "Registration failed. Please try again.";
			}

			return {
				success: false,
				error: {
					message: parsedError.message,
					code: parsedError.code,
					fieldErrors: parsedError.fieldErrors,
				},
			};
		}

		// Update auth store with newly created user
		useAuthStore.getState().setAuth({
			user: responseData.user,
			accessToken: responseData.api_key,
			refreshToken: responseData.refresh_token,
			accessExpiresAt: responseData.access_expires_at || "",
			refreshExpiresAt: responseData.refresh_expires_at || "",
		});

		return { success: true, data: responseData };
	} catch (error) {
		console.error("Signup error:", error);
		return {
			success: false,
			error: {
				message: "An unexpected error occurred. Please try again.",
			},
		};
	}
}

/**
 * Clear all application state (called on logout)
 */
function clearAllState(): void {
	useAuthStore.getState().clearAuth();
	useStreamingStore.getState().reset();
	useTeammateUIStore.getState().reset();
	useOnboardingStore.getState().resetOnboarding();
	getQueryClient()?.clear();
}

/**
 * Logout the current user
 */
export async function logout(): Promise<void> {
	try {
		// Call logout endpoint FIRST while token is still available
		await authClient.POST("/api/v1/auth/logout", {}).catch((error) => {
			// Log but don't block logout - server invalidation is best-effort
			console.error("Logout API call failed (non-critical):", error);
		});

		// Clear all application state
		clearAllState();
	} catch (error) {
		console.error("Logout error:", error);
		// Always clear all state even if API call fails
		clearAllState();
	}
}

/**
 * Initialize auth state from localStorage
 * This is called automatically when the auth store module is imported
 * You typically don't need to call this manually
 */
export function initializeAuth(): void {
	useAuthStore.getState().restoreAuth();
}

/**
 * Wait for auth to be initialized
 * Used in route guards to ensure auth state is ready before checking authentication
 * This ensures no flash of login page when refreshing protected routes
 */
export async function waitForAuthInit(): Promise<void> {
	// Wait for the auth store initialization promise to resolve
	await getAuthInitPromise();
}
